/*
 * Utility functions to operate on CHOOSE_TRANSFER_INPUT pane state
 */
import _ from 'underscore';

import {assert} from 'Asserts';
import {DEFAULT_LEGACY_MEASUREMENT} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema, TagSchemaContainableType} from 'constants/RegistryConstants';
import {ContainableSummary, ContainerSummary, ModalPane} from 'constants/SampleConstants';
import {SampleRestrictionStatus} from 'constants/SampleStatusConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {CreateAndRegisterBioentityArgs} from 'registry/create-bioentity/CreateBioentityUtil';
import {
  ChooseTransferInputPaneState,
  TransferModalState,
  TransferPaneSampleStatusState,
} from 'samples/transfers/TransferModalUtil';
import ErrorSubclass from 'util/ErrorSubclass';

import * as InputSampleConstants from './InputSampleConstants';
import {TYPE as InputSampleType} from './InputSampleConstants';

/*
 * Get inputSample for ContainerTransfers from the chosen input (bioentity, batch, or container)
 */
export function getInputSample(
  inputBioentity: BioentitySummary | null,
  inputContainer: ContainerSummary | null
): ContainableSummary | ContainerSummary | null {
  return inputBioentity || inputContainer;
}

export function getDefaultTransferPaneSampleStatusState(): TransferPaneSampleStatusState {
  return {
    canProceedOnSampleStatus: false,
    selectedSampleIds: [],
    shouldShowStatusWarnings: false,
    samples: null,
    warningInputOutputType: null,
  };
}

export function getDefaultPaneState(): TransferModalState {
  return {
    pane: ModalPane.CHOOSE_TRANSFER_INPUT,
    inputType: InputSampleConstants.TYPE.EXISTING_BATCH,
    inputBioentity: null,
    createAndRegisterBioentityArgs: CreateBioentityUtil.getDefaultCreateAndRegisterBioentityArgs(),
    inputContainer: null,
    inputContainable: null,
    initialOutputContainerStateById: {},
    selectedInputContainer: null,
    inputContainableConcentration: DEFAULT_LEGACY_MEASUREMENT,
    outputType: ModelTypeConstants.CONTAINER,
    outputPlate: null,
    outputCells: [],
    outputContainerSummaries: [],
    outputContainers: [],
    selectedOutputContainers: [],
    containerContents: null,
    containerTransfers: null,
    confirmTransferQuantitiesData: null,
    confirmConcentrationsData: null,
    errorMessage: null,
    errors: [],
    shouldCalculateConcentrations: true,
    areSolidUnitsInvolved: false,
    isTransferring: false,
    transferCancelled: false,
    confirmedOutputContainerIds: null,
    inputSampleStatusState: getDefaultTransferPaneSampleStatusState(),
    outputSampleStatusState: getDefaultTransferPaneSampleStatusState(),
    useInferredSampleControls: true,
    restrictionStatus: SampleRestrictionStatus.UNRESTRICTED,
    restrictedSampleUsers: [],
    sampleOwners: [],
    showNameTemplateBanner: false,
  };
}

export function getExistingBioentityId(
  inputType: InputSampleType,
  inputBioentity: BioentitySummary | null
): string | null {
  if (InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(inputType)) {
    return inputBioentity ? inputBioentity.id : null;
  }
  return null;
}

export function getTagSchema(
  inputType: InputSampleType,
  inputBioentity: BioentitySummary | null,
  createAndRegisterBioentityArgs: CreateAndRegisterBioentityArgs
): TagSchema | null {
  if (InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(inputType)) {
    return inputBioentity ? inputBioentity.tagSchema : null;
  }
  if (InputSampleConstants.HAS_NEW_BIOENTITY_TYPES.includes(inputType)) {
    return createAndRegisterBioentityArgs.tagSchema ?? null;
  }
  return null;
}

export function getCompleteButtonText(paneState: {inputType: InputSampleType}): string {
  const {inputType} = paneState;
  if (InputSampleConstants.HAS_NEW_BIOENTITY_TYPES.includes(inputType)) {
    return 'Register';
  }
  if (InputSampleConstants.IS_NEW_BATCH_TYPES.includes(inputType)) {
    return 'Create';
  }
  return 'Next';
}

export function isComplete(paneState: ChooseTransferInputPaneState): boolean {
  const {createAndRegisterBioentityArgs, inputBioentity, inputContainer, inputType} = paneState;
  const isCompleteArray = [];

  if (InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(inputType)) {
    isCompleteArray.push(inputBioentity);
  } else if (InputSampleConstants.HAS_NEW_BIOENTITY_TYPES.includes(inputType)) {
    isCompleteArray.push(CreateBioentityUtil.isBioentityValidForCreation(createAndRegisterBioentityArgs));
  }

  if (InputSampleConstants.IS_CONTAINER_TYPES.includes(inputType)) {
    isCompleteArray.push(inputContainer);
  }

  return _.all(isCompleteArray);
}

export class DataModsModalCancelledException extends ErrorSubclass {}

export async function completeAndGetModalState(
  paneState: TransferModalState
): Promise<Partial<TransferModalState>> {
  const {
    createAndRegisterBioentityArgs,
    inputBioentity,
    inputContainer,
    inputType,
    confirmTransferQuantitiesData,
  } = paneState;
  let completedInputBioentity = null;
  let completedInputContainer = null;

  if (InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(inputType)) {
    completedInputBioentity = inputBioentity;
  } else if (InputSampleConstants.HAS_NEW_BIOENTITY_TYPES.includes(inputType)) {
    completedInputBioentity = await CreateBioentityUtil.createAndRegisterWithDataModsOnMerge(
      createAndRegisterBioentityArgs
    );
    if (completedInputBioentity == null) {
      throw new DataModsModalCancelledException();
    }
  }

  if (InputSampleConstants.IS_CONTAINER_TYPES.includes(inputType)) {
    completedInputContainer = inputContainer;
  }

  // Since the input sample has been created, it is now "existing" in the system. Convert inputType to
  // reflect that, so the user sees the created input sample if they navigate back to the
  // CHOOSE_TRANSFER_INPUT pane.
  let completedInputType;
  if (InputSampleConstants.IS_BIOENTITY_TYPES.includes(inputType)) {
    completedInputType = InputSampleConstants.TYPE.EXISTING_BIOENTITY;
  } else if (InputSampleConstants.IS_BATCH_TYPES.includes(inputType)) {
    completedInputType = InputSampleConstants.TYPE.EXISTING_BATCH;
  } else {
    assert(InputSampleConstants.IS_CONTAINER_TYPES.includes(inputType), `Unexpected inputType: ${inputType}`);
    completedInputType = InputSampleConstants.TYPE.EXISTING_CONTAINER;
  }
  // If this is the first time completing the input pane (confirmTransferQuantitiesData is null), initialize
  // restriction status based on the input container.
  const possibleInputRestrictionState =
    confirmTransferQuantitiesData == null ? getInitialRestrictionStatusState(inputContainer) : {};
  return {
    inputBioentity: completedInputBioentity,
    inputContainer: completedInputContainer,
    ...possibleInputRestrictionState,
    inputType: completedInputType,
    // Default inputContainableConcentration to input containable's concentration or null
    inputContainableConcentration: completedInputBioentity ? DEFAULT_LEGACY_MEASUREMENT : null,
  };
}

/**
 * Return valid input sample type for transfer modal state if it is incorrectly set
 *
 * This function exists to ensure that `inputType` of the transfer modal is set correctly when the user
 * changes the selected entity (if using an existing entity) or schema (if creating a new entity), so that it
 * (inputType) is appropriate for the `containableType` of the schema.
 */
export function getValidInputType(modalState: TransferModalState): InputSampleType {
  const {inputType, inputBioentity, createAndRegisterBioentityArgs} = modalState;
  const hasExistingBioentity = InputSampleConstants.HAS_EXISTING_BIOENTITY_TYPES.includes(inputType);
  const tagSchema = getTagSchema(inputType, inputBioentity, createAndRegisterBioentityArgs);

  if (
    tagSchema?.containableType === TagSchemaContainableType.BATCH &&
    InputSampleConstants.IS_BIOENTITY_TYPES.includes(inputType)
  ) {
    return hasExistingBioentity
      ? InputSampleConstants.TYPE.EXISTING_BATCH
      : InputSampleConstants.TYPE.NEW_BATCH_ON_NEW_BIOENTITY;
  } else if (
    tagSchema?.containableType === TagSchemaContainableType.ENTITY &&
    InputSampleConstants.IS_BATCH_TYPES.includes(inputType)
  ) {
    return hasExistingBioentity
      ? InputSampleConstants.TYPE.EXISTING_BIOENTITY
      : InputSampleConstants.TYPE.NEW_BIOENTITY;
  } else {
    return inputType;
  }
}

/*
 * Return restriction status state initialized from input container if valid.
 *
 * Returns input container's restriction status if it is present & not N/A. Used to initialize the
 * restriction status to restricted if the input is restricted.
 */
export function getInitialRestrictionStatusState(inputContainer?: ContainerSummary | null): {
  restrictionStatus?: SampleRestrictionStatus;
} {
  const maybeInputRestrictionStatus = inputContainer?.restrictionStatus;
  return maybeInputRestrictionStatus != null &&
    maybeInputRestrictionStatus !== SampleRestrictionStatus.NOT_APPLICABLE
    ? {restrictionStatus: maybeInputRestrictionStatus}
    : {};
}
